import React from 'react';

// Components
import WorkspaceRelationship from './WorkspaceRelationship';
import UserBaProfileSnapshot from 'Shared/components/UserBaProfileSnapshot/UserBaProfileSnapshot';
import { WpText } from 'Wpds/components/text';
import RelationshipSample from './RelationshipSample/RelationshipSample';

// Hooks
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useUser } from 'Shared/utils/hooks/use-user';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';
import { useSmac } from 'Shared/utils/hooks/use-smac';

// Styles
import './WorkspaceRelationshipTab.scss';

const WorkspaceRelationshipTab = (): JSX.Element => {
  const { isOneOnOne, otherUserInOneOnOne } = useWorkspace();
  const { slug: currentUserSlug } = useCurrentUser();
  const { user } = useUser({ slug: currentUserSlug });
  const { isPiDemoAccount } = useOrganization();
  const { hasInspireAccess } = useSmac();

  const { referencePatternName: userReferencePatternName } = useBaProfile({
    user,
  });
  const { referencePatternName: otherUserInOneOnOneReferencePatternName } = useBaProfile({ user: otherUserInOneOnOne });

  if (!isOneOnOne) return null;

  const baseClassName = 'WorkspaceRelationshipTab';

  if (!hasInspireAccess && !isPiDemoAccount) {
    return (
      <div className={`${baseClassName}__sample-container`}>
        <RelationshipSample />
      </div>
    );
  }

  // Either the current user, or the other use in the workspace does not have a completed BA
  if (!userReferencePatternName || !otherUserInOneOnOneReferencePatternName)
    return (
      <div className={`${baseClassName}__no-ba-container`}>
        <WpText variant="bodyXl" bold={true}>
          Reveal your relationship with a few clicks
        </WpText>
        <WpText variant="bodyMd" color="gray-600">
          Both you and {otherUserInOneOnOne.firstName} must first complete the Behavioral Assessment. Once you do,
          you’ll learn your biggest relationship strengths, caution areas, and tips for success.
        </WpText>
        <div className={`${baseClassName}__no-ba-container__profiles`}>
          {!userReferencePatternName && (
            // Current User does not have a BA profile
            <div className={`${baseClassName}__no-ba-container__profiles__profile`}>
              <UserBaProfileSnapshot user={user} showDescription={false} showDivider={false} showExternalLink={false} />
            </div>
          )}
          {!otherUserInOneOnOneReferencePatternName && (
            // Other User does not have a BA profile
            <div className={`${baseClassName}__no-ba-container__profiles__profile`}>
              <UserBaProfileSnapshot
                user={otherUserInOneOnOne}
                showDescription={false}
                showDivider={false}
                showExternalLink={false}
                buttonNote={`This will email ${otherUserInOneOnOne.firstName} an invitation to complete the assessment.`}
              />
            </div>
          )}
        </div>
      </div>
    );

  return (
    <div className={baseClassName}>
      <WorkspaceRelationship user={user} otherUser={otherUserInOneOnOne} />
    </div>
  );
};

export default WorkspaceRelationshipTab;
